import * as React from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Upload as KendoUpload } from "@progress/kendo-react-upload";
import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import Papa from "papaparse";

import ErrorBanner from "../../components/ErrorBanner";
import {
  useMutatePostClientAetnaMappingValidate,
  useMutatePostClientAetnaMappingImport,
} from "../../data/services/clientAetnaPlanMappingService";

const REQUIRED_HEADERS = ["ClientID", "AetnaPlan"];
const OPTIONAL_PLAN_HEADERS = ["Plan1", "Plan2", "Plan3"];

const trimCell = (raw) =>
  raw === undefined || raw === null ? "" : String(raw).trim();

// Case-insensitive lookup against Papa's reported headers. Papa itself only
// trims (via transformHeader); matching case here means "clientid" and
// "ClientID" resolve to the same column.
const findHeader = (headers, name) =>
  headers.find((header) => header.toLowerCase() === name.toLowerCase());

/**
 * CSV import for the Client Aetna Plan Mappings admin screen.
 *
 * Parse in the browser, validate on the server (warnings need the plan
 * catalog, which the browser does not have), preview with per-row warnings,
 * then apply. Apply posts the originally parsed rows, not the validator's
 * echo: the server re-canonicalizes with the same helper either way, so
 * sending its own answer back would make the round trip authoritative over
 * the file and hide a divergence instead of surfacing it.
 */
const ClientAetnaPlanMappingsImportModal = ({
  oePeriodId,
  onClose,
  onImported,
}) => {
  const [files, setFiles] = React.useState([]);
  const [parseError, setParseError] = React.useState(null);
  const [fileRows, setFileRows] = React.useState([]);
  const [validatedRows, setValidatedRows] = React.useState([]);
  const [warningRowCount, setWarningRowCount] = React.useState(0);
  const [applying, setApplying] = React.useState(false);

  const validateHandler = useMutatePostClientAetnaMappingValidate();
  const importHandler = useMutatePostClientAetnaMappingImport();

  const resetPreview = () => {
    setFileRows([]);
    setValidatedRows([]);
    setWarningRowCount(0);
  };

  const runValidate = async (rows) => {
    try {
      const response = await validateHandler.mutateAsync({ oePeriodId, rows });
      setValidatedRows(response?.data?.rows || []);
      setWarningRowCount(response?.data?.warningRowCount || 0);
    } catch {
      // Surfaced by <ErrorBanner handler={validateHandler} />.
    }
  };

  const onAddFileHandler = (event) => {
    setFiles(event.newState);
    setParseError(null);
    resetPreview();

    const rawFile = event.newState[0]?.getRawFile?.();
    if (!rawFile) return;

    Papa.parse(rawFile, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header) => (header || "").trim(),
      complete: (results) => {
        const headers = results.meta?.fields || [];

        // An empty file has no header row at all - there is nothing to
        // report as a missing column, only an absence of rows.
        if (headers.length === 0) {
          setParseError("The file does not contain any rows.");
          return;
        }

        const clientIdHeader = findHeader(headers, "ClientID");
        if (!clientIdHeader) {
          setParseError(
            "The file is missing a required 'ClientID' column header."
          );
          return;
        }

        const aetnaPlanHeader = findHeader(headers, "AetnaPlan");
        if (!aetnaPlanHeader) {
          setParseError(
            "The file is missing a required 'AetnaPlan' column header."
          );
          return;
        }

        // Plan1-Plan3 are optional: a client may map to fewer than three
        // alternatives, or the file may omit trailing columns entirely.
        const plan1Header = findHeader(headers, "Plan1");
        const plan2Header = findHeader(headers, "Plan2");
        const plan3Header = findHeader(headers, "Plan3");

        const parsedRows = results.data
          .map((row) => ({
            clientId: trimCell(row[clientIdHeader]),
            aetnaPlanId: trimCell(row[aetnaPlanHeader]),
            plan1Id: plan1Header ? trimCell(row[plan1Header]) : "",
            plan2Id: plan2Header ? trimCell(row[plan2Header]) : "",
            plan3Id: plan3Header ? trimCell(row[plan3Header]) : "",
          }))
          // A row blank on both the client and the Aetna plan is filler
          // (a trailing blank line, a stray comma row), not data.
          .filter(
            (row) => row.clientId.length > 0 || row.aetnaPlanId.length > 0
          );

        if (parsedRows.length === 0) {
          setParseError("The file does not contain any rows.");
          return;
        }

        setFileRows(parsedRows);
        runValidate(parsedRows);
      },
      error: () => {
        setParseError("Failed to parse the file. Please check the format.");
      },
    });
  };

  const onRemoveFileHandler = (event) => {
    setFiles(event.newState);
    setParseError(null);
    resetPreview();
  };

  const onDownloadTemplate = () => {
    const csvData = Papa.unparse({
      fields: [...REQUIRED_HEADERS, ...OPTIONAL_PLAN_HEADERS],
      data: [],
    });
    const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "Client Aetna Plan Mappings Import Template.csv";
    link.click();
    // The object URL pins the blob in memory for the life of the document.
    URL.revokeObjectURL(url);
  };

  const handleApply = async () => {
    setApplying(true);

    try {
      // The originally parsed rows, not validatedRows: see the module
      // comment above.
      const response = await importHandler.mutateAsync({
        oePeriodId,
        rows: fileRows,
      });
      setApplying(false);
      onImported(response?.data);
      onClose();
    } catch {
      setApplying(false);
      // Surfaced by <ErrorBanner handler={importHandler} />.
    }
  };

  const droppedCount = validatedRows.filter(
    (row) => row.willImport === false
  ).length;

  // validatedRows.length > 0 requires that validation actually succeeded at
  // least once for the current file: if /validate fails (500, network drop,
  // expired auth), validatedRows stays [] and Apply must not stay enabled on
  // a preview the admin never saw. The server echoes back every row it was
  // sent - warned, dropped, or clean - so this does not conflict with either
  // of those cases keeping Apply enabled.
  const canApply =
    !applying &&
    !validateHandler.isLoading &&
    fileRows.length > 0 &&
    validatedRows.length > 0;

  const clientCell = (props) => {
    const { suppliedClientId, clientId } = props.dataItem;
    const resolved =
      suppliedClientId && clientId && suppliedClientId !== clientId;
    return <td>{resolved ? `${suppliedClientId} → ${clientId}` : clientId}</td>;
  };

  const warningsCell = (props) => {
    const { warnings, willImport } = props.dataItem;
    const dropped = willImport === false;
    const hasWarnings = (warnings || []).length > 0;

    return (
      <td
        style={{
          color: dropped ? "#a94442" : hasWarnings ? "#856404" : "",
          fontWeight: dropped ? "bold" : "normal",
        }}
      >
        {dropped && <span>Will not be imported. </span>}
        {(warnings || []).join(" ")}
      </td>
    );
  };

  return (
    <Dialog
      title={"Import client Aetna plan mappings"}
      onClose={onClose}
      width={900}
    >
      <ErrorBanner handler={validateHandler} />
      <ErrorBanner handler={importHandler} />
      <p>
        Upload a CSV with <b>ClientID</b> and <b>AetnaPlan</b> columns, and up
        to three optional alternative-plan columns (<b>Plan1</b>, <b>Plan2</b>
        , <b>Plan3</b>). Each client&apos;s mappings for this season are
        replaced by what is uploaded.
      </p>
      <p>
        <Button themeColor={"info"} fillMode="flat" onClick={onDownloadTemplate}>
          Download Template
        </Button>
      </p>
      <KendoUpload
        autoUpload={false}
        showActionButtons={false}
        files={files}
        multiple={false}
        onAdd={onAddFileHandler}
        onRemove={onRemoveFileHandler}
        restrictions={{ allowedExtensions: [".csv"] }}
      />
      {parseError && (
        <div
          style={{
            backgroundColor: "#f8d7da",
            padding: 10,
            marginTop: 10,
            borderRadius: ".25rem",
            color: "#721c24",
          }}
        >
          {parseError}
        </div>
      )}
      {validatedRows.length > 0 && (
        <>
          <div data-testid="import-summary" style={{ marginTop: 20 }}>
            <b>{validatedRows.length}</b> rows, <b>{warningRowCount}</b> with
            warnings
            {droppedCount > 0 && (
              <>
                , <b>{droppedCount}</b> will not be imported
              </>
            )}
            .
          </div>
          {droppedCount > 0 && (
            <div
              data-testid="dropped-rows-warning"
              style={{
                backgroundColor: "#f8d7da",
                padding: 10,
                marginTop: 10,
                borderRadius: ".25rem",
                color: "#721c24",
              }}
            >
              {droppedCount} row(s) will be dropped by the import. They are
              marked below.
            </div>
          )}
          <div style={{ marginTop: 10 }}>
            <Grid data={validatedRows} style={{ maxHeight: "350px" }}>
              <Column field="rowNumber" title="Row" width={70} />
              <Column title="Client" width={160} cell={clientCell} />
              <Column field="aetnaPlanId" title="Aetna Plan" width={120} />
              <Column field="plan1Id" title="Plan 1" width={100} />
              <Column field="plan2Id" title="Plan 2" width={100} />
              <Column field="plan3Id" title="Plan 3" width={100} />
              <Column title="Warnings" cell={warningsCell} />
            </Grid>
          </div>
        </>
      )}
      <div style={{ paddingTop: 20 }}></div>
      <DialogActionsBar layout={"end"}>
        <Button onClick={onClose}>{"Cancel"}</Button>
        <Button
          type={"submit"}
          themeColor={"success"}
          disabled={!canApply}
          onClick={handleApply}
        >
          {"Apply"}
          {applying && <span className="k-icon k-i-loading"></span>}
        </Button>
      </DialogActionsBar>
    </Dialog>
  );
};

export default ClientAetnaPlanMappingsImportModal;
